import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { environment } from "src/environments/environment";
import io from "socket.io-client";
import { CredentialsService } from "../core/credentials.service";
import { Subject, BehaviorSubject } from "rxjs";

export enum SocketEvents {
  WaitingRoom = "WAITING_ROOM",
  FollowUpRoom = "FOLLOW_UP_ROOM",
  RemoveFromWaitingRoom = "REMOVE_FROM_WAITING_ROOM",
  RemoveFromFollowUpRoom = "REMOVE_FROM_FOLLOW_UP_ROOM",
  Document = "DOCUMENT",
  Connect = "connect",
  Connection = "connection",
  Disconnect = "disconnect",
  AgentScheduleRoom = "AGENT_SCHEDULE_ROOM",
  AgentScheduledRoom = "AGENT_SCHEDULED_ROOM",
  AgentRescheduleRoom = "AGENT_RESCHEDULE_ROOM",
  RemoveFromAgentScheduleRoom = "REMOVE_FROM_AGENT_SCHEDULE_ROOM",
  RemoveFromAgentScheduledRoom = "REMOVE_FROM_AGENT_SCHEDULED_ROOM",
  RemoveFromAgentRescheduleRoom = "REMOVE_FROM_AGENT_RESCHEDULE_ROOM",
  DoctorSpecialistWaitingRoom = "DOCTOR_SPECIALIST_WAITING_ROOM",
  RemoveFromDoctorSpecialistWaitingRoom = "REMOVE_FROM_DOCTOR_SPECIALIST_WAITING_ROOM",
}

export type SocketEvent = MessageEvent & {
  operation: SocketEvents;
  consultation: any;
};

export enum ConnectionStatus {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  RECONNECTING = "reconnecting",
  FAILED = "failed",
}

@Injectable({
  providedIn: "root",
})
export class SocketioService implements OnDestroy {
  private socket: any;
  private connection$: Subject<SocketEvent>;

  // Nuevas propiedades para manejo de reconexión
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // 1 segundo inicial
  private maxReconnectDelay = 30000; // 30 segundos máximo
  private reconnectTimer: any;
  private isReconnecting = false;
  private manualDisconnect = false;

  // Subject para notificar el estado de conexión
  public connectionStatus$ = new BehaviorSubject<ConnectionStatus>(
    ConnectionStatus.DISCONNECTED,
  );

  constructor(private credentialsService: CredentialsService) {}

  ngOnDestroy(): void {
    this.disconnect();
  }

  connect(type: string = "doctor"): Subject<SocketEvent> {
    if (this.socket) {
      this.disconnect();
    }

    if (this.connection$) {
      this.connection$.complete();
      this.connection$ = null;
    }

    this.connection$ = new Subject<SocketEvent>();
    this.socket = io(String(environment.socket_url).trim(), {
      query: {
        token: this.credentialsService.credentials.token,
      },
      transports: ["polling"],
      forceNew: true,
    });

    this.setupConnectionEvents(type);
    this.subscribeToAllEvents(this.connection$);

    return this.connection$;
  }

  disconnect() {
    this.manualDisconnect = true;
    this.clearReconnectTimer();

    if (this.socket) {
      this.unsubscribeAllEvents();
      this.socket.disconnect();
      this.socket = null;
    }

    if (this.connection$) {
      this.connection$.complete();
      this.connection$ = null;
    }

    this.connectionStatus$.next(ConnectionStatus.DISCONNECTED);
  }

  emit(event: SocketEvents): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, JSON.stringify({}));
    }
  }

  // Nueva función para obtener el estado de conexión
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus$.value;
  }

  // Nueva función para reconexión manual
  forceReconnect(): void {
    this.reconnectAttempts = 0;
    this.manualDisconnect = false;
    this.attemptReconnect();
  }

  private setupConnectionEvents(type: string = "doctor"): void {
    console.log("setupConnectionEvents", type);
    if (!this.socket) return;

    this.socket.on("connect", () => {
      this.socket.emit(SocketEvents.Connection);
      this.connectionStatus$.next(ConnectionStatus.CONNECTED);
      this.reconnectAttempts = 0;
      this.isReconnecting = false;
      this.clearReconnectTimer();
      switch (type) {
        case "doctor":
          this.socket.emit(SocketEvents.WaitingRoom);
          this.socket.emit(SocketEvents.FollowUpRoom);
          break;
        case "specialist":
          this.socket.emit(SocketEvents.DoctorSpecialistWaitingRoom);
          break;
        case "agent":
          this.socket.emit(SocketEvents.AgentScheduleRoom);
          this.socket.emit(SocketEvents.AgentScheduledRoom);
          this.socket.emit(SocketEvents.AgentRescheduleRoom);
          break;
      }
    });

    this.socket.on("disconnect", (reason: string) => {
      console.log("Desconectado del servidor:", reason);
      this.connectionStatus$.next(ConnectionStatus.DISCONNECTED);

      // Solo intentar reconectar si no fue una desconexión manual
      if (!this.manualDisconnect) {
        this.attemptReconnect();
      }
    });

    this.socket.on("connect_error", (error: any) => {
      console.error("Error de conexión:", error);
      if (!this.manualDisconnect) {
        this.attemptReconnect();
      }
    });
  }

  private attemptReconnect(): void {
    if (this.isReconnecting || this.manualDisconnect) {
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Máximo número de intentos de reconexión alcanzado");
      this.connectionStatus$.next(ConnectionStatus.FAILED);
      return;
    }

    this.isReconnecting = true;
    this.connectionStatus$.next(ConnectionStatus.RECONNECTING);
    this.reconnectAttempts++;

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    );

    console.log(
      `Intentando reconectar en ${delay}ms (intento ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimer = setTimeout(() => {
      if (!this.manualDisconnect) {
        console.log(`Intento de reconexión ${this.reconnectAttempts}`);
        this.isReconnecting = false;
        this.connect();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private subscribeToEvent(
    event: SocketEvents,
    callback: (data: any) => void,
  ): void {
    if (this.socket) {
      this.socket.off(event);
      this.socket.on(event, callback);
    }
  }

  private subscribeToAllEvents(connection$: Subject<MessageEvent>): void {
    if (this.socket) {
      // Removemos los eventos connect y disconnect de aquí porque ya los manejamos en setupConnectionEvents
      this.subscribeToEvent(SocketEvents.WaitingRoom, (data) => {
        data.operation = SocketEvents.WaitingRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.FollowUpRoom, (data) => {
        data.operation = SocketEvents.FollowUpRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.Document, (data) => {
        data.operation = SocketEvents.Document;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.RemoveFromWaitingRoom, (data) => {
        data.operation = SocketEvents.RemoveFromWaitingRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.RemoveFromFollowUpRoom, (data) => {
        data.operation = SocketEvents.RemoveFromFollowUpRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.AgentScheduleRoom, (data) => {
        data.operation = SocketEvents.AgentScheduleRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.AgentScheduledRoom, (data) => {
        data.operation = SocketEvents.AgentScheduledRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(SocketEvents.AgentRescheduleRoom, (data) => {
        data.operation = SocketEvents.AgentRescheduleRoom;
        connection$.next(data);
      });

      this.subscribeToEvent(
        SocketEvents.DoctorSpecialistWaitingRoom,
        (data) => {
          data.operation = SocketEvents.DoctorSpecialistWaitingRoom;
          connection$.next(data);
        },
      );

      this.subscribeToEvent(
        SocketEvents.RemoveFromAgentScheduleRoom,
        (data) => {
          data.operation = SocketEvents.RemoveFromAgentScheduleRoom;
          connection$.next(data);
        },
      );

      this.subscribeToEvent(
        SocketEvents.RemoveFromAgentScheduledRoom,
        (data) => {
          data.operation = SocketEvents.RemoveFromAgentScheduledRoom;
          connection$.next(data);
        },
      );

      this.subscribeToEvent(
        SocketEvents.RemoveFromAgentRescheduleRoom,
        (data) => {
          data.operation = SocketEvents.RemoveFromAgentRescheduleRoom;
          connection$.next(data);
        },
      );

      this.subscribeToEvent(
        SocketEvents.RemoveFromDoctorSpecialistWaitingRoom,
        (data) => {
          data.operation = SocketEvents.RemoveFromDoctorSpecialistWaitingRoom;
          connection$.next(data);
        },
      );
    }
  }

  private unsubscribeAllEvents(): void {
    if (this.socket) {
      this.socket.off(SocketEvents.Connect);
      this.socket.off(SocketEvents.Disconnect);
      this.socket.off(SocketEvents.WaitingRoom);
      this.socket.off(SocketEvents.FollowUpRoom);
      this.socket.off(SocketEvents.Document);
      //this.socket.off(SocketEvents.RemoveFromWaitingRoom);
      this.socket.off(SocketEvents.RemoveFromFollowUpRoom);
      this.socket.off(SocketEvents.AgentScheduleRoom);
      this.socket.off(SocketEvents.AgentScheduledRoom);
      this.socket.off(SocketEvents.AgentRescheduleRoom);
      this.socket.off(SocketEvents.DoctorSpecialistWaitingRoom);
      this.socket.off(SocketEvents.RemoveFromAgentScheduleRoom);
      this.socket.off(SocketEvents.RemoveFromAgentScheduledRoom);
      this.socket.off(SocketEvents.RemoveFromAgentRescheduleRoom);
      this.socket.off(SocketEvents.RemoveFromDoctorSpecialistWaitingRoom);
    }
  }
}
